import { assertNever, proveNever } from "@glideapps/ts-necessities";
import { areEqual, definedMap, hasOwnProperty, iterableEnumerate, mapToObject } from "collection-utils";
import fromPairs from "lodash/fromPairs";
import sortBy from "lodash/sortBy";
import sortedUniq from "lodash/sortedUniq";
import zip from "lodash/zip";
import { makeComponentPositionID } from "../common/component-indexes";
import { ImageAspectRatio } from "../common/components/image-types";
import type {
    AppDescription,
    AppFeatures,
    AppKind,
    SerializedApp,
    SerializedComponent,
    SerializedComponentsObject,
    SerializedComputedColumn,
    SerializedMenuItem,
    SerializedPivot,
    SerializedRootScreen,
    SerializedScreen,
    UserFeatures,
} from "../common/components/SerializedApp";
import { getAppKind, getAppTabs, ScreenFlag } from "../common/components/SerializedApp";
import type { QuotaBannerRequirements, UserProfileTableInfo } from "../common/components/types";
import { MenuItemPurpose } from "../common/components/types";
import type { TableName, UniversalTableName } from "../common/database-strings";
import { areTableNamesEqual, commentsTableName } from "../common/database-strings";
import type { BuilderAction, BuilderActionsForApp } from "../common/Database/builder-actions";
import type { EminenceFlags } from "../common/Database/eminence";
import { logError, logInfo } from "../common/debug-print";
import type {
    ActionDescription,
    ArrayColumnType,
    ArrayScreenDescription,
    ArrayScreenItemType,
    ArrayTransform,
    ChatScreenDescription,
    ClassScreenDescription,
    ColumnAssignment,
    ColumnType,
    ComponentDescription,
    EditScreenDescription,
    InputOutputTables,
    ScreenDescription,
    ShoppingCartScreenDescription,
    SpanCoordsFloat,
    TabDescription,
    TableColumn,
    TableGlideType,
    TableRefGlideType,
    TypeSchema,
    UniversalTableRefGlideType,
} from "../common/description";
import {
    findTable,
    getPrimitiveNonHiddenColumns,
    getSpanCoordsForComponents,
    getTableColumn,
    getTableName,
    getTableRefTableName,
    GlobalVariableNames,
    isColumnWriteable,
    isComputedColumn,
    isFormScreen,
    isPrimitiveType,
    isSingleRelationType,
    makeInputOutputTables,
    makeTableName,
    makeTableRef,
    MutatingScreenKind,
    ScreenDescriptionKind,
} from "../common/description";
import { getFeatureSetting } from "../common/feature-settings";
import { rowIndexColumnName } from "../common/google-sheets";
import type { SourceMetadata } from "../common/source-metadata";
import { getMutatingKindForScreen } from "../component-utils/mutating-screen-kind";
import {
    addClassScreenName,
    arrayScreenName,
    buyScreenName,
    classScreenName,
    editClassScreenName,
    emailAuthScreenName,
    isAddClassScreenName,
    isEditClassScreenName,
    isFreeScreenName,
    sendAppFeedbackScreenName,
    shareScreenName,
    userProfileScreenName,
    webViewScreenName,
} from "../component-utils/screen-names";
import {
    assert,
    defined,
    definedMapWithDefault,
    EqualitySet,
    exceptionToString,
    mapFilterUndefined,
    panic,
} from "../support";
import type { ArrayMap } from "../support/array-map";
import { handlerForActionKind } from "./actions";
import { handlerForArrayScreenFormat } from "./array-screens";
import { sortPropertyPropertyName, sortPropertyVariableName } from "./array-screens/array-screen";
import { ListItemAccessoryPosition, ListItemFlags } from "./array-screens/list-item-flags";
import { makeUserProfileArgsForCommentsAndChat } from "./builder-user-profile";
import type { PivotContent, ScreenWithMenuItemsContent, SearchBarData } from "./builder-utils";
import {
    compileFilters,
    defaultValuesForEditScreen,
    getColumnNamesAssignedInEditScreen,
    getColumnNamesEditedInScreen,
    makeErrorScreen,
    makePostCommentAction,
    makeQuotaBannerMessage,
    makeRowIndexOrRowID,
    makeSearchBar,
    makeUniqueIDForArrayScreen,
    makeValueForSourceColumn,
    rawSearchContextPropertyName,
    screenErrorInternal,
    screenErrorMissingTable,
    searchConditionsForClassScreen,
    transformArray,
    trimmedSearchVariableName,
} from "./builder-utils";
import { makeReusableComponents } from "./component-functions";
import type { ComponentBuilder, ExistingAppDescriptionContext, UserProfileRowBuild } from "./components";
import { builderJSCompiledFormulas, compiledJSCallbacks } from "./components";
import { Appearance } from "./components/button-appearance";
import { handlerForPropertyKind } from "./components/description-handlers";
import { Mood } from "./components/mood";
import { getColumnProperty, getScreenProperty, getStringProperty } from "./components/property-utils";
import { TextEntrySize } from "./components/text-entry-types";
import {
    getColumnAssignments,
    getMenuScreenNames,
    getScreenFilter,
    inputOutputTablesForClassOrArrayScreen,
    makeInputOutputTablesForFormOnSubmitAction,
} from "./description-utils";
import { getEditOnSubmitActions } from "./form-on-submit";
import { compileFormula, getPropertyWithColumnFormat } from "./formulas";
import { makeUniqueIDForColumnFormula } from "./formulas/compiler";
import { handlerForComponentKind } from "./handlers";
import type { LetBinding } from "./serialized-component";
import { fetchLinkColumn, SerializedElementBuilder } from "./serialized-component";
import { getUserProfileTableInfo } from "./user-profile-info";

// FIXME: This really doesn't belong here
export const globalChatTopic = "$$ GLOBAL_CHAT $$";

const dynamicFilterScreenName = "filter-and-sort";
const choiceScreenName = "choice";
const multiChoiceScreenName = "multi-choice";
const overlayEditorScreenName = "overlay-editor";
const signaturePadScreenName = "signature-pad";
const codeScannerScreenName = "code-scanner";
const liveLinearBarcodeScannerScreenName = "live-linear-barcode-scanner";
const eventPickerScreenName = "event-picker";
const reportAppScreenName = "report-app";

function componentValidFlagName(index: number): string {
    return `c${index}`;
}

function visibilityFlagName(index: number): string {
    return `v${index}`;
}

class ColumnTrackingSerializedElementBuilder extends SerializedElementBuilder {
    // [table name, column name]
    private readonly _columnsUsed: EqualitySet<[string, string]> = new EqualitySet();

    public getProperty(
        property: string,
        objectData: SerializedComponent,
        table: TableGlideType | TableName
    ): SerializedComponent {
        this._columnsUsed.add([getTableName(table).name, property]);
        return super.getProperty(property, objectData, table);
    }

    public getColumnsUsed(): Iterable<[string, string]> {
        return this._columnsUsed.values();
    }
}

abstract class AppBuilder implements ComponentBuilder {
    private readonly _components: Map<string, SerializedComponent> = new Map();
    private readonly _screens: Map<string, SerializedScreen> = new Map();
    private readonly _screensUsedInApp: Set<string> = new Set();
    private readonly _screensToBuild: string[] = [];
    private readonly _tablesUsed: TableName[] = [];
    private _errors: string[] = [];
    private _currentlyBuildingScreenName: string | undefined;

    public readonly b = new ColumnTrackingSerializedElementBuilder();

    private readonly _getContextData = this.b.getNonTableProperty("data", this.b.getContext);
    private readonly _getScreenTitle = this.b.getNonTableProperty("title", this.b.getContext);

    // FIXME: Shouldn't this just take a `ComponentConfiguratorContext`?
    constructor(
        private readonly _adc: ExistingAppDescriptionContext,
        public readonly eminenceFlags: EminenceFlags,
        public readonly isBuilder: boolean,
        private readonly _appFeatures: AppFeatures,
        private readonly _withHighlightComponent: boolean,
        private readonly _printErrors: boolean
    ) {}

    public get appID(): string {
        return this._adc.appID;
    }

    public get appDescription(): AppDescription {
        return this._adc.appDescription;
    }

    public get appKind(): AppKind {
        return getAppKind(this.appDescription);
    }

    public get builderActions(): BuilderActionsForApp {
        return this._adc.builderActions;
    }

    public getBuilderAction(id: string): BuilderAction | undefined {
        return this._adc.getBuilderAction(id);
    }

    public get schema(): TypeSchema {
        return this._adc.schema;
    }

    public get sourceMetadata(): readonly SourceMetadata[] | undefined {
        return this._adc.sourceMetadata;
    }

    public get columnsHiddenForNotebooks(): ArrayMap<TableName, ReadonlySet<string>> | undefined {
        return undefined;
    }

    public info(...args: readonly any[]): void {
        if (this._printErrors) {
            logInfo(...args);
        }
    }

    public error(...args: readonly any[]): void {
        if (this._printErrors) {
            logError(...args);
        }
        this._errors.push(args.map(a => a.toString()).join(" "));
    }

    public get errors(): readonly string[] | undefined {
        if (this._errors.length === 0) return undefined;
        return this._errors;
    }

    public abstract getCompiledJSCode(name: string): SerializedComponent | undefined | false;

    public get userFeatures(): UserFeatures {
        return this._appFeatures;
    }

    public findTable(ref: UniversalTableName | UniversalTableRefGlideType | undefined): TableGlideType | undefined {
        return this._adc.findTable(ref);
    }

    public makeLetsFromProperties(properties: readonly string[]): readonly LetBinding[] {
        const { b } = this;
        return properties.map(p => [p, b.getNonTableProperty(p, b.getContext)]);
    }

    public makeRootContext(): SerializedComponent {
        return this.b.data({});
    }

    private addScreenToBuild(screenName: string): void {
        // logInfo(this._currentlyBuildingScreenName ?? "?", "=>", screenName);
        this._screensToBuild.push(screenName);
    }

    public classScreenDescriptionForTable(tableRef: TableRefGlideType): ClassScreenDescription | undefined {
        const screenName = classScreenName(tableRef);
        const desc = this.appDescription.screenDescriptions[screenName];
        if (desc === undefined) return undefined;
        if (desc.kind !== ScreenDescriptionKind.Class) {
            return panic(`Screen ${screenName} was expected to be a class screen`);
        }
        return desc;
    }

    private compoundTypeScreenName(type: UniversalTableRefGlideType | ArrayColumnType): string | undefined {
        switch (type.kind) {
            case "table-ref":
                const table = this.findTable(getTableRefTableName(type));
                if (table === undefined) return undefined;
                return classScreenName(getTableName(table));
            case "array":
                return arrayScreenName(this, type.items);
            default:
                return assertNever(type);
        }
    }

    public makePushScreen(
        screenNameOrType: string | ColumnType,
        title: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined,
        screenData: SerializedComponent
    ): SerializedComponent | undefined {
        const { b } = this;

        let screenName: string | undefined;
        if (typeof screenNameOrType === "string") {
            screenName = screenNameOrType;
        } else {
            if (isPrimitiveType(screenNameOrType)) return undefined;
            screenName = this.compoundTypeScreenName(screenNameOrType);
        }

        if (screenName === undefined) return undefined;

        this.addScreenToBuild(screenName);
        const screenCtx = b.screenContext(
            b.getForwardee(screenData),
            definedMap(title, t => ({ title: t }))
        );
        return b.pushBuilderScreen(screenName, screenCtx, undefined, itemID);
    }

    private getAddOrEditScreen(
        itemType: TableName,
        makeScreenName: (t: TableName) => string
    ): { screenName: string; desc: EditScreenDescription } | undefined {
        const table = findTable(this.schema, itemType);
        if (table === undefined) return undefined;

        const screenName = makeScreenName(itemType);
        const desc = this.appDescription.screenDescriptions[screenName];
        if (desc === undefined) {
            this.error(`Edit screen ${screenName} not defined`);
            return undefined;
        }

        if (desc.kind !== ScreenDescriptionKind.Class) {
            this.error(`Edit screen ${screenName} is not a class screen`);
            return undefined;
        }

        this.addScreenToBuild(screenName);

        return { screenName, desc: desc as EditScreenDescription };
    }

    public makePushAddItemScreen(
        itemType: ArrayScreenItemType,
        arrayData: SerializedComponent
    ): SerializedComponent | undefined {
        const { b } = this;

        if (!isSingleRelationType(itemType)) {
            this.error(`Cannot make edit screen for type ${itemType.kind}`);
            return undefined;
        }

        const editScreen = this.getAddOrEditScreen(getTableRefTableName(itemType), addClassScreenName);
        if (editScreen === undefined) return undefined;
        const { screenName, desc } = editScreen;

        const tables = inputOutputTablesForClassOrArrayScreen(desc, this.schema);
        if (tables === undefined) return undefined;

        return b.pushModalBuilderScreen(
            screenName,
            // FIXME: Remove this let
            b.let(
                [["array", arrayData]],
                b.object({
                    acceptTitle: b.localizedString("add"),
                    // FIXME: Don't pass these in
                    withDelete: b.constant(false),
                })
            ),
            undefined,
            true,
            false
        );
    }

    public makePushEditItemScreen(
        itemType: TableName,
        itemData: SerializedComponent,
        title: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = this;

        const editScreen = this.getAddOrEditScreen(itemType, editClassScreenName);
        if (editScreen === undefined) return undefined;

        return b.pushModalBuilderScreen(
            editScreen.screenName,
            b.let(
                [["originalObject", itemData]],
                b.if(
                    b.isNotEmpty(b.getVariable("originalObject")),
                    b.object({
                        editObjectBase: b.getVariable("originalObject"),
                        acceptTitle: b.localizedString("done"),
                        title,
                    })
                )
            ),
            title,
            true,
            true
        );
    }

    public makeSequenceFromActions(
        descs: ReadonlyArray<ActionDescription>,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        fromArrayScreen: boolean
    ): SerializedComponent | undefined {
        const { b } = this;

        const actions = mapFilterUndefined(descs, desc => {
            const handler = handlerForActionKind(desc.kind);
            if (handler === undefined) return undefined;
            const descriptor = handler.getDescriptor(desc, tables, this, mutatingScreenKind);
            if (descriptor === undefined) return undefined;
            if (descriptor.needsScreenContext === true) {
                assert(screenContext !== undefined);
            }
            return handler.makeAction(
                this,
                desc,
                tables,
                screenContext,
                outputContext,
                mutatingScreenKind,
                undefined,
                undefined,
                undefined,
                fromArrayScreen
            );
        });
        if (actions.length === 0) return undefined;
        return b.sequence(actions);
    }

    public registerTableUsed(tableName: TableName): void {
        if (findTable(this.schema, tableName) === undefined) {
            logError("Used table does not exist", tableName.name);
            return;
        }

        if (this._tablesUsed.some(n => areTableNamesEqual(n, tableName))) return;

        this._tablesUsed.push(tableName);
    }

    public fetchLinkColumn(column: TableColumn, columnCtx: SerializedComponent): SerializedComponent {
        let type: ColumnType = column.type;
        if (type.kind === "array") {
            type = type.items;
        }
        assert(isSingleRelationType(type), "Link column to be fetched is not a reference");
        const table = this.findTable(type);
        if (table !== undefined) {
            this.registerTableUsed(getTableName(table));
        }

        return fetchLinkColumn(this.b, column, columnCtx, false);
    }

    public fetchTableRows(tableName: TableName, forBuilder: boolean): SerializedComponent {
        const { b } = this;
        this.registerTableUsed(tableName);
        return b.fetchTableRows("default", tableName.name, forBuilder);
    }

    private fetchTableRowsForScreen(screenName: string, table: TableGlideType): SerializedComponent {
        const rows = this.fetchTableRows(getTableName(table), false);

        const desc = this.appDescription.screenDescriptions[screenName];
        // array screens do the transforms themselves
        if (desc === undefined || desc.kind === ScreenDescriptionKind.Array) return rows;

        const { filter, transforms } = getScreenFilter(desc);

        return transformArray(
            this,
            table,
            rows,
            undefined,
            undefined,
            filter,
            false,
            transforms,
            makeUniqueIDForArrayScreen(screenName)
        );
    }

    private screenForArray(screenName: string, desc: ArrayScreenDescription): ScreenWithMenuItemsContent {
        const { b } = this;

        let ctx: SerializedComponent;
        if (desc.fetchesData === true) {
            if (!isSingleRelationType(desc.type)) {
                return panic("Can only fetch table types");
            }
            const table = this.findTable(desc.type);
            if (table === undefined) {
                return makeErrorScreen(b, screenName, screenErrorMissingTable);
            }
            ctx = this.fetchTableRowsForScreen(screenName, table);
        } else {
            ctx = this._getContextData;
        }
        const handler = handlerForArrayScreenFormat(desc.format);
        if (handler === undefined) {
            return makeErrorScreen(b, screenName, "This screen has a style that's not supported anymore.");
        }
        return handler.makeScreenContent(
            this,
            screenName,
            desc,
            ctx,
            this._getScreenTitle,
            b.getNonTableProperty("arrayScreenRow", b.getContext),
            makeUniqueIDForArrayScreen(screenName)
        );
    }

    public makeVisibilityConditions(
        table: TableGlideType,
        components: readonly ComponentDescription[],
        context: SerializedComponent
    ): readonly (SerializedComponent | undefined)[] {
        return components.map(({ visibilityFilters }) => compileFilters(this, table, context, visibilityFilters ?? []));
    }

    private makeVisibilityVariables(
        table: TableGlideType,
        desc: ClassScreenDescription,
        context: SerializedComponent
    ): {
        visibilityLets: readonly LetBinding[];
        visibilityVariables: readonly (SerializedComponent | undefined)[];
    } {
        const { b } = this;

        const visibilityConditions = this.makeVisibilityConditions(table, desc.components, context);
        return {
            visibilityLets: mapFilterUndefined(visibilityConditions, (c, i) =>
                definedMap(c, v => [visibilityFlagName(i), v])
            ),
            visibilityVariables: visibilityConditions.map((c, i) =>
                definedMap(c, () => b.getVariable(visibilityFlagName(i)))
            ),
        };
    }

    private makeValidationVariables(
        screen: ClassScreenDescription,
        outputContext: SerializedComponent
    ):
        | {
              validationLets: readonly LetBinding[];
              validationVariables: readonly (SerializedComponent | undefined)[];
              haveNonEmptyStoppers: boolean;
          }
        | undefined {
        const { b } = this;

        const table = this.findTable(screen.type);
        if (table === undefined) return undefined;

        const flags: (SerializedComponent | undefined)[] = [];

        let haveNonEmptyStoppers = false;
        for (const [i, desc] of iterableEnumerate(screen.components)) {
            const handler = handlerForComponentKind(desc.kind);
            if (handler === undefined) continue;

            if (handler.isNonEmptyValidationStopper) {
                haveNonEmptyStoppers = true;
            }

            if (!handler.needValidation(desc)) continue;

            let flag = handler.makeValidationFlag(b, desc, outputContext, table);
            if (flag === undefined) {
                flag = b.data(true);
            }

            flags[i] = flag;
        }

        return {
            validationLets: mapFilterUndefined(flags, (f, i) => definedMap(f, v => [componentValidFlagName(i), v])),
            validationVariables: flags.map((f, i) => definedMap(f, () => b.getVariable(componentValidFlagName(i)))),
            haveNonEmptyStoppers,
        };
    }

    // `allowSubmitEmpty` will disable validation that requires at least one field
    // to be non-empty.
    private makeValidationProperties(
        screen: ClassScreenDescription,
        editObject: SerializedComponent,
        editedProperties: readonly string[] | undefined,
        visibilityConditions: readonly (SerializedComponent | undefined)[]
    ):
        | {
              validationLets: readonly LetBinding[];
              validationVariables: readonly (SerializedComponent | undefined)[];
              isValidFlag: SerializedComponent;
          }
        | undefined {
        const { b } = this;

        const maybeValidationVariables = this.makeValidationVariables(screen, editObject);
        if (maybeValidationVariables === undefined) return undefined;

        const { validationLets, validationVariables, haveNonEmptyStoppers } = maybeValidationVariables;

        let isValid: SerializedComponent | undefined;
        if (!haveNonEmptyStoppers && editedProperties !== undefined && editedProperties.length > 0) {
            isValid = b.builtinCall("ObjectHasNonEmptyProperties", [editObject, b.constant(editedProperties)]);
        }

        if (validationVariables.some(v => v !== undefined)) {
            const pairs: LetBinding[] = [];
            for (const [i, v] of iterableEnumerate(validationVariables)) {
                if (v === undefined) continue;
                const c = visibilityConditions[i];
                pairs.push([componentValidFlagName(i), definedMapWithDefault(c, v, d => b.or(v, b.not(d, true)))]);
            }
            const flagsObject = b.object(fromPairs(pairs));
            const allTrue = b.builtinCall("AllObjectPropertiesAreTrue", [flagsObject]);
            isValid = definedMapWithDefault(isValid, allTrue, v => b.and(v, allTrue));
        }

        if (isValid === undefined) return undefined;

        return {
            validationLets: [...validationLets, ["isValid", isValid]],
            validationVariables,
            isValidFlag: b.getVariable("isValid"),
        };
    }

    private hideComponentIfNecessary(
        ui: SerializedComponent,
        searchNotActive: SerializedComponent | undefined,
        visibilityCondition: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = this;

        let condition = searchNotActive;
        if (visibilityCondition !== undefined) {
            if (condition === undefined) {
                condition = visibilityCondition;
            } else {
                condition = b.and(condition, visibilityCondition);
            }
        }

        if (condition === undefined) {
            return ui;
        } else {
            return b.if(condition, ui, b.emptyUI);
        }
    }

    public makeComponents(
        screenName: string,
        componentDescriptions: readonly ComponentDescription[],
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        // This must not depend on the context, i.e. should be a `getVariable`
        outputContext: SerializedComponent,
        searchVariable: SerializedComponent | undefined,
        searchNotActive: SerializedComponent | undefined,
        visibilityConditions: readonly (SerializedComponent | undefined)[],
        validationVariables: readonly (SerializedComponent | undefined)[]
    ): {
        components: readonly SerializedComponent[];
        componentIDs: readonly string[];
        spanCoordsFloat: readonly SpanCoordsFloat[];
        quotaRequirements: QuotaBannerRequirements;
    } {
        const { b } = this;

        const components: SerializedComponent[] = [];
        const componentIDs: string[] = [];
        const spanCoordsFloat: SpanCoordsFloat[] = [];
        let quotaRequirements: QuotaBannerRequirements = { needListQuota: false, needMapQuota: false };

        for (const [i, [pd, coords]] of iterableEnumerate(getSpanCoordsForComponents(componentDescriptions))) {
            const handler = handlerForComponentKind(pd.kind);
            if (handler === undefined) continue;

            const uniqueID = `component-${i.toString()}-${screenName}`;
            let ui: SerializedComponent;
            try {
                ui =
                    handler.entryForTableColumn?.(
                        this,
                        pd,
                        tables,
                        mutatingScreenKind,
                        validationVariables[i],
                        outputContext,
                        searchVariable,
                        searchNotActive,
                        uniqueID
                    ) ?? b.emptyUI;
            } catch (e: unknown) {
                logError("Error building component", e);
                ui = b.errorUI;
            }
            const componentID = makeComponentPositionID(screenName, [i]);
            if (this._withHighlightComponent && !handler.isGlideManaged) {
                ui = b.ui("HighlightComponent", {
                    child: ui,
                    componentID: b.constant(componentID),
                });
            }
            let condition: SerializedComponent | undefined;
            if (searchNotActive !== undefined && handler.makeNeedsSearch(this, pd, tables) === undefined) {
                condition = searchNotActive;
            }
            ui = this.hideComponentIfNecessary(ui, condition, visibilityConditions[i]);

            components.push(ui);
            componentIDs.push(componentID);
            spanCoordsFloat.push(coords);

            const reqs = handler.getQuotaBannerRequirements(pd);
            quotaRequirements = {
                needListQuota: quotaRequirements.needListQuota || reqs.needListQuota,
                needMapQuota: quotaRequirements.needMapQuota || reqs.needMapQuota,
            };
        }

        return { components, componentIDs, spanCoordsFloat, quotaRequirements };
    }

    // FIXME: Use `getStructureLayoutForComponents` instead of this algorithmic abomination
    private makeComponentLayout(
        allSerializedComponents: readonly SerializedComponent[],
        componentIDs: readonly string[],
        spanCoordsFloat: readonly SpanCoordsFloat[]
    ): SerializedComponent {
        const { b } = this;

        if (!getFeatureSetting("multiColumnLayout")) {
            return b.column(allSerializedComponents);
        }

        const allComponents = zip(allSerializedComponents, componentIDs, spanCoordsFloat);

        const serializedSpans: SerializedComponent[] = [];
        const spanIndexes = sortedUniq(sortBy(spanCoordsFloat.map(([s]) => s)));
        for (const spanIndex of spanIndexes) {
            const coordsInSpan = spanCoordsFloat.filter(([s]) => spanIndex === s);
            const columnIndexes = sortedUniq(sortBy(coordsInSpan.map(([, c]) => c))).slice(0, 4);
            assert(columnIndexes.length > 0);
            const width = 12 / columnIndexes.length;
            const serializedColumns: SerializedComponent[] = [];
            for (const columnIndex of columnIndexes) {
                const coordsInColumn = coordsInSpan.filter(([, c]) => columnIndex === c);
                const componentIndexes = sortedUniq(sortBy(coordsInColumn.map(([, , i]) => i)));
                const serializedComponents: SerializedComponent[] = [];
                const serializedComponentIDs: SerializedComponent[] = [];
                for (const componentIndex of componentIndexes) {
                    const componentsWithIDs = mapFilterUndefined(allComponents, ([c, i, s]) => {
                        const [cs, cc, ci] = defined(s);
                        if (cs !== spanIndex || cc !== columnIndex || ci !== componentIndex) return undefined;
                        return [defined(c), defined(i)] as const;
                    });
                    assert(componentsWithIDs.length === 1);
                    const [component, componentID] = componentsWithIDs[0];
                    serializedComponents.push(component);
                    serializedComponentIDs.push(b.constant(componentID));
                }
                serializedColumns.push(
                    b.object({
                        width: b.constant(width),
                        components: b.list(serializedComponents),
                        componentIDs: b.list(serializedComponentIDs),
                    })
                );
            }
            serializedSpans.push(b.list(serializedColumns));
        }

        return b.ui("Spans", { layout: b.list(serializedSpans) });
    }

    private makeTitleForClassScreen(
        screenName: string,
        desc: ClassScreenDescription,
        table: TableGlideType,
        context: SerializedComponent
    ): SerializedComponent | undefined {
        const { b } = this;

        if (desc.title === undefined) return undefined;

        const columnName = getColumnProperty(desc.title);
        if (columnName !== undefined) {
            return getPropertyWithColumnFormat(this, table, columnName, context);
        } else {
            const titleString = getStringProperty(desc.title);
            if (titleString === undefined) {
                logError("Invalid title property in screen", screenName, desc.title);
            } else if (titleString.trim() !== "") {
                return b.data(titleString);
            }
        }
        return undefined;
    }

    private makeClassScreenComponents(
        screenName: string,
        desc: ClassScreenDescription,
        ctxModifier: SerializedComponent | undefined,
        extraComponents: readonly SerializedComponent[]
    ):
        | {
              table: TableGlideType;
              component: SerializedComponent;
              title: SerializedComponent | undefined;
              topBar: SerializedComponent | undefined;
              ctxModifier: SerializedComponent | undefined;
              quotaRequirements: QuotaBannerRequirements;
          }
        | string {
        const { b } = this;

        const table = findTable(this.schema, desc.type);
        if (table === undefined) return screenErrorMissingTable;

        const tables = makeInputOutputTables(table);
        const needSearchConditions = searchConditionsForClassScreen(this, desc, tables);
        const needSearch =
            needSearchConditions.length > 0
                ? b.bindContextToValue(b.or(...needSearchConditions), b.singletonItem(this._getContextData))
                : undefined;
        let searchBar: SearchBarData | undefined;
        if (needSearch !== undefined) {
            searchBar = makeSearchBar(
                b,
                undefined,
                needSearch,
                definedMap(desc.searchPlaceholder, p => b.constant(getStringProperty(p))),
                false
            );
        }

        const searchNotActiveVariable = needSearch !== undefined ? b.getVariable("searchNotActive") : undefined;
        const visibilityConditions = this.makeVisibilityConditions(table, desc.components, b.getContext);
        const { components, spanCoordsFloat, componentIDs, quotaRequirements } = this.makeComponents(
            screenName,
            desc.components,
            tables,
            undefined,
            b.getVariable("outputContext"),
            searchBar?.trimmedSearchVariable,
            searchNotActiveVariable,
            visibilityConditions,
            []
        );

        let topBar: SerializedComponent | undefined;
        const lets: LetBinding[] = [["outputContext", b.singletonItem(this._getContextData)]];
        const component = b.bindContextToValue(
            this.makeComponentLayout([...components, ...extraComponents], componentIDs, spanCoordsFloat),
            b.getVariable("outputContext")
        );
        if (searchBar !== undefined) {
            topBar = searchBar.topBar;
            ctxModifier = b.let(
                searchBar.searchLets,
                b.amendObject(ctxModifier ?? b.getContext, undefined, ["data", "title"], {
                    [rawSearchContextPropertyName]: searchBar.rawSearchVariable,
                    [trimmedSearchVariableName]: searchBar.trimmedSearchVariable,
                })
            );
            lets.push(
                [trimmedSearchVariableName, b.getNonTableProperty(trimmedSearchVariableName, b.getContext)],
                ["searchNotActive", b.stringEmpty(searchBar.trimmedSearchVariable)]
            );
        }

        const title = this.makeTitleForClassScreen(screenName, desc, table, b.singletonItem(this._getContextData));

        return { table, component: b.let(lets, component), title, topBar, ctxModifier, quotaRequirements };
    }

    private addDataToContextModifier(data: SerializedComponent): SerializedComponent {
        const { b } = this;
        return b.amendObject(b.getContext, undefined, ["title"], { data });
    }

    // FIXME: Also don't assign to columns that are assigned via input components
    public makeColumnValues(
        tables: InputOutputTables,
        assignments: readonly ColumnAssignment[],
        alreadyAssigned: ReadonlySet<string> | undefined,
        defaultValues: readonly [string, SerializedComponent][],
        screenContext: SerializedComponent | undefined,
        undefinedIfEmpty: boolean
    ): SerializedComponentsObject {
        const { b } = this;

        let columnValues: SerializedComponentsObject = {};
        for (const { destColumn, value } of assignments) {
            if (
                columnValues[destColumn] !== undefined ||
                (alreadyAssigned !== undefined && alreadyAssigned.has(destColumn))
            ) {
                continue;
            }

            const column = getTableColumn(tables.output, destColumn);
            if (column === undefined || !isColumnWriteable(column, tables.output, true, true)) continue;

            const result = handlerForPropertyKind(value.kind).makeValueForPropertyDescription(
                b,
                value,
                undefinedIfEmpty,
                column.type,
                sc => {
                    const v = makeValueForSourceColumn(
                        this,
                        sc,
                        screenContext,
                        tables.input,
                        undefined,
                        undefined,
                        false
                    );
                    if (v?.column === undefined) return undefined;
                    if (!isPrimitiveType(v.column.type)) return undefined;
                    return v.value;
                }
            );
            if (result === undefined) continue;
            columnValues[column.name] = result;
        }
        columnValues = { ...columnValues, ...fromPairs(defaultValues) };
        return columnValues;
    }

    private makeEditMenuItem(
        desc: ClassScreenDescription,
        table: TableGlideType,
        ctx: SerializedComponent
    ): SerializedMenuItem | undefined {
        const { b } = this;

        if (desc.canEdit !== true) return undefined;

        // FIXME: Once we have captured lets, make one for the `ctx`.  Right
        // now this is hard to do because of the way the `ctxModifier` gets
        // constructed.  If the screen fetches data, it might not have a
        // `data` to begin with, and then we're amending it one by one.
        const onTap = this.makePushEditItemScreen(getTableRefTableName(desc.type), ctx, undefined);
        if (onTap === undefined) return undefined;

        const isEnabled = b.isPropertyDefined(rowIndexColumnName, ctx, table);
        const isVisible = definedMap(
            compileFilters(this, table, b.getVariable("singleton"), desc.canEditFilters ?? []),
            c => b.let([["singleton", ctx]], c)
        );

        return {
            title: b.localizedString("edit"),
            icon: b.icon("00-01-glide-edit"),
            onTap,
            isEnabled,
            isVisible,
            purpose: b.constant(MenuItemPurpose.EditRow),
        };
    }

    private screenForClass(screenName: string, desc: ClassScreenDescription): ScreenWithMenuItemsContent {
        const { b } = this;

        let inputCtxModifier: SerializedComponent | undefined;
        if (desc.fetchesData === true) {
            const fetchTable = this.findTable(desc.type);
            if (fetchTable === undefined) return makeErrorScreen(b, screenName, screenErrorMissingTable);

            const rows = this.fetchTableRowsForScreen(screenName, fetchTable);
            inputCtxModifier = this.addDataToContextModifier(b.listNth(rows, 0));
        }

        const maybeComponents = this.makeClassScreenComponents(screenName, desc, inputCtxModifier, []);
        if (typeof maybeComponents === "string") return makeErrorScreen(b, screenName, maybeComponents);

        const { table, component, title, topBar, ctxModifier, quotaRequirements } = maybeComponents;

        const menuItems: SerializedMenuItem[] = [];

        const editItem = this.makeEditMenuItem(desc, table, b.singletonItem(this._getContextData));
        if (editItem !== undefined) {
            menuItems.push(editItem);
        }

        return {
            pivotComponents: [
                {
                    componentName: screenName,
                    component,
                    menuItems,
                },
            ],
            additionalComponents: undefined,
            ctxModifier,
            title: title ?? this._getScreenTitle,
            topBar,
            pivotPicker: undefined,
            quotaBannerMessage: makeQuotaBannerMessage(b, quotaRequirements),
            flags: [],
        };
    }

    private editScreenForClass(screenName: string, desc: EditScreenDescription): ScreenWithMenuItemsContent {
        const { b } = this;

        const mutatingScreenKind = getMutatingKindForScreen(screenName, desc);
        if (mutatingScreenKind !== MutatingScreenKind.EditScreen) {
            return panic(`Tried to create edit screen with invalid name: ${screenName}`);
        }

        const tables = inputOutputTablesForClassOrArrayScreen(desc, this.schema);
        if (tables === undefined) {
            return makeErrorScreen(b, screenName, screenErrorMissingTable);
        }

        const editedProperties = getColumnNamesEditedInScreen(desc, tables, this, undefined);
        const allProperties = new Set(
            getPrimitiveNonHiddenColumns(tables.output)
                .filter(c => !isComputedColumn(c) && c.isUserSpecific !== true)
                .map(c => c.name)
        );

        const outputContext = b.getVariable("outputContext");

        const { visibilityLets, visibilityVariables } = this.makeVisibilityVariables(
            tables.output,
            desc,
            outputContext
        );
        const validationProperties = this.makeValidationProperties(
            desc,
            outputContext,
            !areEqual(editedProperties, allProperties) ? undefined : Array.from(editedProperties),
            visibilityVariables
        );

        let content = b.bindContextToValue(
            b.column(
                this.makeComponents(
                    screenName,
                    desc.components,
                    tables,
                    mutatingScreenKind,
                    outputContext,
                    undefined,
                    undefined,
                    visibilityVariables,
                    validationProperties?.validationVariables ?? []
                ).components
            ),
            b.getVariable("editObjectBase")
        );

        // FIXME: Why are we looking for the row index column in the input, not the
        // output table, and why are we not using `getTableColumn`?
        if (desc.canDelete === true && tables.input.columns.find(c => c.name === rowIndexColumnName) !== undefined) {
            let condition = b.getVariable("withDelete");
            condition = compileFilters(
                this,
                tables.input,
                b.getVariable("editObjectBase"),
                desc.canDeleteFilters ?? [],
                condition
            );
            const button = b.if(
                condition,
                b.button(
                    b.localizedString("deleteItem"),
                    Appearance.Simple,
                    Mood.Danger,
                    undefined,
                    b.getVariable("onDelete"),
                    {
                        confirm: true,
                        confirmStyle: "delete",
                        confirmTitle: b.localizedString("areYouSureYouWantToDelete"),
                        confirmDesc: b.localizedString("actionCannotBeUndone"),
                        cancelText: b.localizedString("cancel"),
                        acceptText: b.localizedString("delete"),
                    }
                )
            );
            content = b.column([content, button]);
        }

        const columnValues = this.makeColumnValues(
            tables,
            getColumnAssignments(desc),
            editedProperties,
            [],
            b.getVariable("editObjectBase"),
            true
        );
        const outputTableName = getTableName(tables.output);

        let editObject = b.clonePrimitives(
            b.getVariable("editObjectBase"),
            Array.from(editedProperties).map(p => [p, undefined])
        );
        if (Object.keys(columnValues).length > 0) {
            editObject = b.primitive("MergeObjects", { first: editObject, second: b.object(columnValues) });
        }
        editObject = b.builtinCall(
            "AddValueProducersToData",
            [
                editObject,
                b.getVariable("editObjectBase"),
                b.constant(
                    tables.output.columns
                        .filter(
                            c =>
                                !editedProperties.has(c.name) &&
                                columnValues[c.name] === undefined &&
                                !isComputedColumn(c)
                        )
                        .map(c => c.name)
                ),
            ],
            true
        );
        editObject = b.addComputedColumnsToData(outputTableName, editObject);

        const allAssignedColumns = getColumnNamesAssignedInEditScreen(desc, this, mutatingScreenKind);
        const columnNames = definedMap(allAssignedColumns, b.constant);

        const editObjectBase = b.getNonTableProperty("editObjectBase", b.getContext);
        const onAccept = [
            b.setColumnsInRow(
                outputTableName.name,
                makeRowIndexOrRowID(b, tables.output, undefined, b.getVariable("editObjectBase")),
                b.builtinCall(
                    "ExtractChangedProperties",
                    [b.getVariable("editObjectBase"), outputContext, columnNames ?? b.constant(null)],
                    true
                ),
                false
            ),
        ];

        const successAction = this.makeSequenceFromActions(
            getEditOnSubmitActions(desc),
            makeInputOutputTablesForFormOnSubmitAction(tables),
            outputContext,
            outputContext,
            // ##onSubmitMutatingScreenKind:
            // We're pretending that this is not a mutating screen, so that
            // set actions actually write to the backend, as opposed to just
            // writing into the context.  This might have unintended side
            // effects.
            undefined,
            false
        );
        onAccept.push(b.popModalScreen);
        if (successAction !== undefined) {
            onAccept.push(successAction);
        }

        const rowIndexOrRowID = makeRowIndexOrRowID(b, tables.output, undefined, b.getVariable("editObjectBase"));
        const deleteAction = b.deleteRow(outputTableName, rowIndexOrRowID);

        return {
            pivotComponents: [
                {
                    componentName: screenName,
                    component: content,
                    menuItems: [
                        {
                            title: b.getVariable("acceptTitle"),
                            icon: b.icon("00-01-glide-check"),
                            style: b.constant("platform-accept"),
                            purpose: b.constant(MenuItemPurpose.SaveRow),
                            onTap: b.sequence(onAccept),
                            isEnabled: definedMap(validationProperties, v => v.isValidFlag),
                        },
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["editObjectBase", editObjectBase],
                ["outputContext", editObject],
                ["withDelete", b.canWriteRow(rowIndexOrRowID)],
                ["onDelete", b.sequence([b.popModalScreen, b.popScreen, deleteAction])],
                ["title", b.getNonTableProperty("title", b.getContext)],
                ...this.makeLetsFromProperties(["acceptTitle"]),
                ...visibilityLets,
                ...definedMapWithDefault(validationProperties, [], v => v.validationLets),
            ]),
            title: b.withDefault(
                b.getVariable("title"),
                this.makeTitleForClassScreen(screenName, desc, tables.input, b.getVariable("editObjectBase")) ??
                    b.localizedString("editItem")
            ),
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: makeQuotaBannerMessage(b, { needListQuota: true, needMapQuota: false }),
            flags: [ScreenFlag.IsEditScreen],
        };
    }

    public makePushFormScreen(
        screenName: string,
        title: SerializedComponent,
        screenContext: SerializedComponent
    ): SerializedComponent | undefined {
        const { b } = this;

        const screenDesc = this.appDescription.screenDescriptions[screenName];
        if (screenDesc === undefined || screenDesc.kind !== ScreenDescriptionKind.Class || screenDesc.isForm !== true) {
            return undefined;
        }

        this.addScreenToBuild(screenName);

        return b.pushModalBuilderScreen(
            screenName,
            // The push action is disabled if the context is empty, but we
            // want forms to always open, so we have to supply an empty
            // context by default.
            b.withDefault(screenContext, b.object({})),
            title,
            "offline-action-queue",
            false
        );
    }

    public makePushFreeScreen(
        screenName: string,
        title: string | SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = this;

        const screenDesc = this.appDescription.screenDescriptions[screenName];
        if (screenDesc === undefined) {
            return undefined;
        }

        this.addScreenToBuild(screenName);

        if (typeof title === "string") {
            title = b.constant(title);
        }

        return b.pushBuilderScreen(screenName, b.object({ title }), undefined, undefined);
    }

    public makePushMenuScreen(
        menuID: string,
        primaryKey: SerializedComponent,
        defaultScreenName: string,
        title: SerializedComponent | undefined,
        screenData: SerializedComponent
    ): SerializedComponent | undefined {
        const { b } = this;

        if (this.appDescription.screenDescriptions[defaultScreenName] === undefined) return undefined;

        this.addScreenToBuild(defaultScreenName);
        for (const screenName of getMenuScreenNames(this.appDescription, menuID)) {
            this.addScreenToBuild(screenName);
        }

        const screenCtx = b.screenContext(
            b.getForwardee(screenData),
            definedMap(title, t => ({ title: t }))
        );

        return b.pushMenuScreen(menuID, primaryKey, defaultScreenName, screenCtx);
    }

    private addOrFormScreenForClass(screenName: string, desc: EditScreenDescription): ScreenWithMenuItemsContent {
        const { b } = this;

        const mutatingScreenKind = getMutatingKindForScreen(screenName, desc);
        if (
            mutatingScreenKind !== MutatingScreenKind.AddScreen &&
            mutatingScreenKind !== MutatingScreenKind.FormScreen
        ) {
            return panic(`Tried to create add/form screen with invalid name: ${screenName}`);
        }
        const isForm = mutatingScreenKind === MutatingScreenKind.FormScreen;

        const tables = inputOutputTablesForClassOrArrayScreen(desc, this.schema);
        if (tables === undefined) {
            return makeErrorScreen(b, screenName, screenErrorMissingTable);
        }

        const outputContext = b.getVariable("outputContext");

        const successActionInner = this.makeSequenceFromActions(
            getEditOnSubmitActions(desc),
            makeInputOutputTablesForFormOnSubmitAction(tables),
            b.getVariable("addedRow"),
            b.getVariable("addedRow"),
            // FIXME: We're pretending that this is not a mutating screen, so
            // that set actions actually write to the backend, as opposed to
            // just writing into the context.  This might have unintended side
            // effects.
            undefined,
            false
        );
        const successAction = definedMap(successActionInner, a => b.holdAction(b.let([["addedRow", b.getContext]], a)));

        const { visibilityLets, visibilityVariables } = this.makeVisibilityVariables(
            tables.output,
            desc,
            outputContext
        );

        const editedProperties = getColumnNamesEditedInScreen(desc, tables, this, MutatingScreenKind.FormScreen);
        const validationProperties = this.makeValidationProperties(
            desc,
            outputContext,
            Array.from(editedProperties),
            visibilityVariables
        );

        const content = b.bindContextToValue(
            b.column(
                this.makeComponents(
                    screenName,
                    desc.components,
                    tables,
                    MutatingScreenKind.FormScreen,
                    outputContext,
                    undefined,
                    undefined,
                    visibilityVariables,
                    validationProperties?.validationVariables ?? []
                ).components
            ),
            b.getVariable("parentContext")
        );

        const allAssignedColumns = getColumnNamesAssignedInEditScreen(desc, this, MutatingScreenKind.FormScreen);

        const outputTableName = getTableName(tables.output);
        const defaultValues = defaultValuesForEditScreen(this, desc, tables);
        const editObject = b.addComputedColumnsToData(
            outputTableName,
            b.object(
                this.makeColumnValues(
                    tables,
                    getColumnAssignments(desc),
                    editedProperties,
                    defaultValues,
                    b.getContext,
                    true
                )
            )
        );

        return {
            pivotComponents: [
                {
                    componentName: screenName,
                    component: content,
                    menuItems: [
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                        {
                            title: isForm ? b.localizedString("submit") : b.localizedString("add"),
                            icon: b.icon("00-01-glide-check"),
                            style: b.constant("platform-accept"),
                            purpose: b.constant(MenuItemPurpose.AddRow),
                            onTap: b.getVariable("onSubmit"),
                            isEnabled: definedMap(validationProperties, vp => vp.isValidFlag),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["parentContext", b.getContext],
                ["outputContext", editObject],
                [
                    "onSubmit",
                    b.sequence([
                        b.popModalScreen,
                        b.addRowToTable(
                            "default",
                            outputTableName,
                            outputContext,
                            definedMap(allAssignedColumns, b.constant),
                            successAction
                        ),
                    ]),
                ],
                ...this.makeLetsFromProperties(isForm ? [] : ["acceptTitle"]),
                ...visibilityLets,
                ...definedMapWithDefault(validationProperties, [], vp => vp.validationLets),
            ]),
            title: isForm
                ? undefined
                : this.makeTitleForClassScreen(screenName, desc, tables.input, outputContext) ??
                  b.localizedString("newItem"),
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: makeQuotaBannerMessage(b, { needListQuota: true, needMapQuota: false }),
            flags: [isForm ? ScreenFlag.IsFormScreen : ScreenFlag.IsAddItemScreen],
        };
    }

    // `items` is the list of potential filters, from which the user can select one or more,
    // `values` is the current list of filters.  If it's empty, no filtering should happen,
    // otherwise all rows should be shown which match at least one of the `values`.
    public makePushFilterScreen(
        items: SerializedComponent | undefined,
        values: SerializedComponent | undefined,
        filterColumn: SerializedComponent | undefined,
        sortProperties: SerializedComponent | undefined,
        sortProperty: SerializedComponent | undefined,
        sortAscending: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = this;

        assert(items !== undefined || sortProperties !== undefined);

        const title =
            items === undefined
                ? b.localizedString("sortBy")
                : sortProperties === undefined
                ? b.localizedString("filterBy")
                : b.localizedString("filterAndSort");
        return b.pushModalBuilderScreen(
            dynamicFilterScreenName,
            b.object({
                items,
                values,
                filterColumn,
                sortProperties,
                [sortPropertyVariableName]: sortProperty,
                sortAscending,
            }),
            title,
            false,
            false
        );
    }

    public makePushOverlayEditorScreen(
        value: SerializedComponent,
        title: SerializedComponent,
        placeholder: SerializedComponent,
        doneText: SerializedComponent,
        doneIcon: SerializedComponent,
        doneStyle: SerializedComponent,
        allowEmpty: boolean,
        onChange: SerializedComponent
    ): SerializedComponent {
        const { b } = this;

        return b.pushModalBuilderScreen(
            overlayEditorScreenName,
            b.object({
                value,
                placeholder,
                doneText,
                doneIcon,
                doneStyle,
                onChange,
                allowEmpty: b.constant(allowEmpty),
            }),
            title,
            true,
            false
        );
    }

    public makePushChoiceScreen(
        value: SerializedComponent,
        choices: SerializedComponent,
        title: SerializedComponent | undefined,
        isRequired: SerializedComponent,
        isMulti: boolean,
        onChange: SerializedComponent
    ): SerializedComponent {
        const { b } = this;

        return b.pushModalBuilderScreen(
            isMulti ? multiChoiceScreenName : choiceScreenName,
            b.object({
                value,
                choices,
                isRequired,
                onChange,
            }),
            title,
            true,
            false
        );
    }

    public makePushSignaturePadScreen(
        message: SerializedComponent,
        signatureURL: SerializedComponent,
        onChange: SerializedComponent | undefined,
        validFlag: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = this;

        return b.pushModalBuilderScreen(
            signaturePadScreenName,
            b.object({ message, signatureURL, onChange, validFlag }),
            b.localizedString("signature"),
            true,
            true
        );
    }

    public makePushCodeScannerScreen(
        standards: SerializedComponent,
        acceptOnFirst: SerializedComponent,
        output: SerializedComponent,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = this;
        return b.pushModalBuilderScreen(
            codeScannerScreenName,
            b.object({ standards, acceptOnFirst, output, onSuccess }),
            b.localizedString("scanCode"),
            true,
            true
        );
    }

    public makePushLiveLinearCodeScannerScreen(
        output: SerializedComponent,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = this;
        return b.pushModalBuilderScreen(
            liveLinearBarcodeScannerScreenName,
            b.object({ output, onSuccess }),
            b.localizedString("scanCode"),
            true,
            true
        );
    }

    public makePushEventPickerScreen(
        startValue: SerializedComponent,
        endValue: SerializedComponent,
        events: SerializedComponent | undefined,
        title: SerializedComponent,
        isRequired: SerializedComponent,
        onChange: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = this;

        return b.pushModalBuilderScreen(
            eventPickerScreenName,
            b.object({
                startValue,
                endValue,
                events,
                isRequired,
                onChange: onChange ?? b.noAction(),
            }),
            title,
            true,
            false
        );
    }

    private screenForChat(_desc: ChatScreenDescription): SerializedComponent {
        const { b } = this;

        this.registerTableUsed(commentsTableName);

        const { lets, args: userProfileArgs } = makeUserProfileArgsForCommentsAndChat(this);

        return b.let(
            lets,
            b.ui("Chat", {
                comments: b.fetchComments(b.constant(globalChatTopic), b.constant("asc")),
                onTapNew: makePostCommentAction(
                    this,
                    b.constant(globalChatTopic),
                    "newMessage",
                    "writeMessage",
                    "send",
                    "platform-submit"
                ),
                onDelete: b.action("DeleteComments", { topic: b.constant(globalChatTopic) }),
                topic: b.constant(globalChatTopic),
                ...userProfileArgs,
            })
        );
    }

    public makeScreenContextForTab(tab: TabDescription): SerializedComponent | undefined {
        const { b } = this;

        const screenName = getScreenProperty(tab.screenName);
        if (screenName === undefined) return undefined;

        let { propertyName } = tab;
        if (propertyName === undefined) {
            // FIXME: This is a terrible hack to support old apps which didn't
            // have a property name set on single-screen apps, but which had the
            // single table as a top-level array in their data.
            if (this.schema.tables.length === 1) {
                const tableName = getTableName(this.schema.tables[0]);
                if (tableName.isSpecial) return undefined;

                propertyName = tableName.name;
            } else {
                return definedMap(tab.title, t => b.data({ title: t }));
            }
        }

        const table = this.findTable(makeTableRef(makeTableName(propertyName)));
        if (table === undefined) return undefined;

        const rows = this.fetchTableRowsForScreen(screenName, table);
        // The title must be a `Data`, not a `Constant`, because it gets set directly in the
        // builder when changing a tab title.
        return b.screenContext(
            rows,
            definedMap(tab.title, t => ({ title: b.data(t) }))
        );
    }

    private makeEmailAuthScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return {
            pivotComponents: [
                {
                    componentName: emailAuthScreenName,
                    component: b.ui("EmailAuthentication", {
                        onSuccess: b.getNonTableProperty("onSuccess", b.getContext),
                        onAbort: b.getNonTableProperty("onAbort", b.getContext),
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.getNonTableProperty("onAbort", b.getContext),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: undefined,
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [],
        };
    }

    private screenForShoppingCart(
        screenName: string,
        _desc: ShoppingCartScreenDescription
    ): ScreenWithMenuItemsContent {
        const { b } = this;

        const content = b.column([
            b.ui("List", {
                list: b.getVariable("items"),
                item: b.adaptiveListItem(
                    b.getNonTableProperty("itemName", b.getContext),
                    b.getNonTableProperty("itemDescription", b.getContext),
                    b.formatPrice(
                        b.getNonTableProperty("itemPrice", b.getContext),
                        b.getNonTableProperty("currencyCode", b.getContext)
                    ),
                    b.getNonTableProperty("itemImage", b.getContext),
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    b.constant(
                        ListItemFlags.LargeImage +
                            ListItemFlags.DisableChevron +
                            ListItemFlags.DrawSeparator +
                            ListItemFlags.BigCaption +
                            ListItemFlags.DisableLongPress
                    ),
                    b.if(
                        b.getVariable("isEditing"),
                        b.ui("CircleButtonAccessory", {
                            icon: b.constant("01-43-remove-bold"),
                            onClick: b.sequence([
                                b.action("DeleteRowFromTable", {
                                    dataStoreName: b.constant("local"),
                                    tableName: b.constant("shopping-cart"),
                                    row: b.getContext,
                                }),
                                b.if(
                                    b.not(b.isNonEmptyArray(b.getVariable("items"))),
                                    b.set(b.getVariable("isEditing"), b.constant(false))
                                ),
                            ]),
                        })
                    ),
                    ListItemAccessoryPosition.TopRight,
                    undefined
                ),
                emptyMessage: b.localizedString("yourCartIsEmpty"),
            }),
            b.if(
                b.and(
                    b.and(b.isNonEmptyArray(b.getVariable("items")), b.not(b.getVariable("isEditing"))),
                    b.primitive("AllEqual", {
                        list: b.getVariable("items"),
                        mapper: b.getNonTableProperty("currencyCode", b.getContext),
                    })
                ),
                b.column([
                    b.label(
                        b.joinStrings(
                            b.list([
                                b.localizedString("total"),
                                b.constant(" "),
                                b.formatPrice(
                                    b.sum(b.getVariable("items"), b.getNonTableProperty("itemPrice", b.getContext)),
                                    b.getNonTableProperty("currencyCode", b.listNth(b.getVariable("items"), 0))
                                ),
                            ])
                        )
                    ),
                    b.button(
                        b.localizedString("checkout"),
                        Appearance.Filled,
                        undefined,
                        undefined,
                        b.pushModalBuilderScreen(
                            buyScreenName,
                            b.object({
                                items: b.getVariable("items"),
                                onFinished: b.popModalScreen,
                                onSuccess: b.action("ClearTable", {
                                    dataStoreName: b.constant("local"),
                                    tableName: b.constant("shopping-cart"),
                                }),
                            }),
                            b.localizedString("checkout"),
                            true,
                            true
                        ),
                        {}
                    ),
                ])
            ),
        ]);

        return {
            pivotComponents: [
                {
                    componentName: screenName,
                    component: content,
                    menuItems: [
                        {
                            title: b.if(
                                b.getVariable("isEditing"),
                                b.localizedString("done"),
                                b.localizedString("edit")
                            ),
                            icon: b.if(
                                b.getVariable("isEditing"),
                                b.icon("00-01-glide-check"),
                                b.icon("00-01-glide-edit")
                            ),
                            onTap: b.set(b.getVariable("isEditing"), b.not(b.getVariable("isEditing"))),
                            isEnabled: b.isNonEmptyArray(b.getVariable("items")),
                            style: b.constant("text"),
                            purpose: b.constant(MenuItemPurpose.SetValue),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["items", b.fetchTableRows("local", "shopping-cart", false)],
                ["isEditing", b.data(false)],
            ]),
            title: this._getScreenTitle,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [],
        };
    }

    private makeBuyScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return {
            pivotComponents: [
                {
                    componentName: buyScreenName,
                    component: b.ui("BuyScreen", {
                        items: b.getNonTableProperty("items", b.getContext),
                        onFinished: b.sequence([
                            b.getNonTableProperty("onSuccess", b.getContext),
                            b.getNonTableProperty("onFinished", b.getContext),
                        ]),
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.getNonTableProperty("onFinished", b.getContext),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: undefined,
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable],
        };
    }

    private makeWebViewScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return {
            pivotComponents: [
                {
                    componentName: webViewScreenName,
                    component: b.ui("Webview", {
                        link: b.getNonTableProperty("link", b.getContext),
                        aspectRatio: b.constant(ImageAspectRatio.Fit),
                        title: b.getNonTableProperty("title", b.getContext),
                        allowScrolling: b.constant(true),
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                        {
                            title: b.localizedString("share"),
                            icon: b.icon("01-28-share-1-alternate"),
                            style: b.constant("icon"),
                            onTap: b.showShareSheet(b.getNonTableProperty("link", b.getContext), undefined),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: undefined,
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsFinalTransient],
        };
    }

    private makeDynamicFilterScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return {
            pivotComponents: [
                {
                    componentName: dynamicFilterScreenName,
                    component: b.let(
                        [
                            ["values", b.getNonTableProperty("values", b.getContext)],
                            ["itemsWithoutAll", b.getNonTableProperty("items", b.getContext)],
                            [
                                "items",
                                b.if(
                                    b.isDefined(b.getVariable("itemsWithoutAll")),
                                    b.joinLists(
                                        b.list([
                                            b.object({
                                                name: b.localizedString("all"),
                                                isChecked: b.not(b.isNonEmptyArray(b.getVariable("values"))),
                                                onTap: b.set(b.getVariable("values"), b.constant([])),
                                            }),
                                        ]),
                                        b.map(
                                            b.getVariable("itemsWithoutAll"),
                                            b.let(
                                                [
                                                    ["name", b.getNonTableProperty("name", b.getContext)],
                                                    ["value", b.getNonTableProperty("value", b.getContext)],
                                                    [
                                                        "isChecked",
                                                        b.arrayContains(
                                                            b.getVariable("values"),
                                                            b.getVariable("value")
                                                        ),
                                                    ],
                                                ],
                                                b.object({
                                                    name: b.getVariable("name"),
                                                    isChecked: b.getVariable("isChecked"),
                                                    onTap: b.if(
                                                        b.getVariable("isChecked"),
                                                        b.action("RemoveItems", {
                                                            array: b.getVariable("values"),
                                                            predicate: b.areEqual(b.getContext, b.getVariable("value")),
                                                        }),
                                                        b.action("AppendItem", {
                                                            array: b.getVariable("values"),
                                                            item: b.getVariable("value"),
                                                        })
                                                    ),
                                                })
                                            )
                                        )
                                    )
                                ),
                            ],
                            ["filterColumn", b.getNonTableProperty("filterColumn", b.getContext)],
                            ["sortProperties", b.getNonTableProperty("sortProperties", b.getContext)],
                            [sortPropertyVariableName, b.getNonTableProperty(sortPropertyVariableName, b.getContext)],
                            ["sortAscending", b.getNonTableProperty("sortAscending", b.getContext)],
                        ],
                        b.column([
                            b.ifIsDefined(
                                b.getVariable("items"),
                                b.smallList(
                                    b.getVariable("items"),
                                    b.adaptiveListItem(
                                        b.getNonTableProperty("name", b.getContext),
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        b.getNonTableProperty("onTap", b.getContext),
                                        undefined,
                                        undefined,
                                        b.constant(ListItemFlags.DrawSeparator + ListItemFlags.DisableChevron),
                                        b.if(
                                            b.getNonTableProperty("isChecked", b.getContext),
                                            b.imageAccessory(b.constant("01-check-1"))
                                        ),
                                        ListItemAccessoryPosition.Right,
                                        undefined
                                    ),
                                    b.ifIsDefined(b.getVariable("sortProperties"), b.getVariable("filterColumn")),
                                    b.constant(false)
                                ),
                                b.emptyUI
                            ),
                            b.ifIsDefined(
                                b.getVariable("sortProperties"),
                                b.smallList(
                                    b.getVariable("sortProperties"),
                                    b.adaptiveListItem(
                                        b.getNonTableProperty("name", b.getContext),
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        b.if(
                                            b.areIdentical(
                                                b.getVariable(sortPropertyVariableName),
                                                b.getNonTableProperty(sortPropertyPropertyName, b.getContext)
                                            ),
                                            b.set(
                                                b.getVariable("sortAscending"),
                                                b.not(b.getVariable("sortAscending"))
                                            ),
                                            b.set(
                                                b.getVariable(sortPropertyVariableName),
                                                b.getNonTableProperty(sortPropertyPropertyName, b.getContext)
                                            )
                                        ),
                                        undefined,
                                        undefined,
                                        b.constant(ListItemFlags.DrawSeparator + ListItemFlags.DisableChevron),
                                        b.if(
                                            b.areIdentical(
                                                b.getVariable(sortPropertyVariableName),
                                                b.getNonTableProperty(sortPropertyPropertyName, b.getContext)
                                            ),
                                            b.imageAccessory(
                                                b.if(
                                                    b.getVariable("sortAscending"),
                                                    b.getNonTableProperty("ascIcon", b.getContext),
                                                    b.getNonTableProperty("descIcon", b.getContext)
                                                )
                                            )
                                        ),
                                        ListItemAccessoryPosition.Right,
                                        undefined
                                    ),
                                    b.ifIsDefined(b.getVariable("items"), b.localizedString("sortBy")),
                                    b.constant(false)
                                ),
                                b.emptyUI
                            ),
                        ])
                    ),
                    menuItems: [
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-accept"),
                            onTap: b.popModalScreen,
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: undefined,
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    private makeOverlayEditorScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return {
            pivotComponents: [
                {
                    componentName: overlayEditorScreenName,
                    component: b.ui("OverlayEditor", {
                        value: b.getVariable("valueCopy"),
                        placeholder: b.getVariable("placeholder"),
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                        {
                            title: b.getVariable("doneText"),
                            icon: b.getVariable("doneIcon"),
                            style: b.getVariable("doneStyle"),
                            purpose: b.constant(MenuItemPurpose.SetValue),
                            onTap: b.sequence([
                                b.set(b.getVariable("value"), b.getVariable("valueCopy")),
                                b.getVariable("onChange"),
                                b.popModalScreen,
                            ]),
                            isEnabled: b.or(b.getVariable("allowEmpty"), b.isNotEmpty(b.getVariable("valueCopy"))),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["value", b.getNonTableProperty("value", b.getContext)],
                ["valueCopy", b.clonePrimitive(b.getVariable("value"))],
                ["onChange", b.getNonTableProperty("onChange", b.getContext)],
                ["placeholder", b.getNonTableProperty("placeholder", b.getContext)],
                ["doneText", b.getNonTableProperty("doneText", b.getContext)],
                ["doneIcon", b.getNonTableProperty("doneIcon", b.getContext)],
                ["doneStyle", b.getNonTableProperty("doneStyle", b.getContext)],
                ["allowEmpty", b.getNonTableProperty("allowEmpty", b.getContext)],
            ]),
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    private makeSignaturePadScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return {
            pivotComponents: [
                {
                    componentName: signaturePadScreenName,
                    component: b.ui("SignaturePad", {
                        message: b.getVariable("message"),
                        signatureURL: b.getVariable("signatureURL"),
                        onChange: b.getVariable("onChange"),
                        shouldWriteCV: b.getVariable("shouldWrite"),
                        isWritingCV: b.getVariable("isWriting"),
                        validFlag: b.getVariable("validFlag"),
                        hasPaths: b.getVariable("hasPaths"),
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-check"),
                            style: b.constant("platform-accept"),
                            purpose: b.constant(MenuItemPurpose.SetValue),
                            onTap: b.sequence([
                                b.action("OnNotifyConditionVariable", {
                                    cv: b.getVariable("isWriting"),
                                    action: b.popModalScreen,
                                }),
                                b.action("NotifyConditionVariable", { cv: b.getVariable("shouldWrite") }),
                            ]),
                            isEnabled: b.getVariable("hasPaths"),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["message", b.getNonTableProperty("message", b.getContext)],
                ["signatureURL", b.getNonTableProperty("signatureURL", b.getContext)],
                ["onChange", b.getNonTableProperty("onChange", b.getContext)],
                ["shouldWrite", b.primitive("MakeConditionVariable", {})],
                ["isWriting", b.primitive("MakeConditionVariable", {})],
                ["validFlag", b.getNonTableProperty("validFlag", b.getContext)],
                ["hasPaths", b.clonePrimitive(b.constant(false))],
            ]),
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsSignaturePad, ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    private makeCodeScannerScreen(): ScreenWithMenuItemsContent {
        const { b } = this;
        return {
            pivotComponents: [
                {
                    componentName: codeScannerScreenName,
                    component: b.ui("CodeScanner", {
                        standards: b.getVariable("standards"),
                        acceptOnFirst: b.getVariable("acceptOnFirst"),
                        output: b.getVariable("output"),
                        onSuccess: b.getVariable("onSuccess"),
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["standards", b.getNonTableProperty("standards", b.getContext)],
                ["acceptOnFirst", b.getNonTableProperty("acceptOnFirst", b.getContext)],
                ["output", b.getNonTableProperty("output", b.getContext)],
                ["onSuccess", b.getNonTableProperty("onSuccess", b.getContext)],
            ]),
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    private makeLiveLinearBarcodeScannerScreen(): ScreenWithMenuItemsContent {
        const { b } = this;
        return {
            pivotComponents: [
                {
                    componentName: liveLinearBarcodeScannerScreenName,
                    component: b.ui("LiveLinearBarcodeScanner", {
                        output: b.getVariable("output"),
                        onSuccess: b.getVariable("onSuccess"),
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["output", b.getNonTableProperty("output", b.getContext)],
                ["onSuccess", b.getNonTableProperty("onSuccess", b.getContext)],
            ]),
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    private makeChoiceScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        const { topBar, searchLets, trimmedSearchVariable } = makeSearchBar(
            b,
            undefined,
            b.getVariable("withSearch"),
            undefined,
            true
        );
        return {
            pivotComponents: [
                {
                    componentName: choiceScreenName,
                    component: b.let(
                        [
                            ["noneValue", b.constant({})],
                            ["searchProperties", b.constant(["name"])],
                            [
                                "filteredItems",
                                b.filter(
                                    b.getVariable("choices"),
                                    b.propertiesContainString(
                                        b.getContext,
                                        b.getVariable("searchProperties"),
                                        trimmedSearchVariable
                                    )
                                ),
                            ],
                            [
                                "choices",
                                b.if(
                                    b.getNonTableProperty("isRequired", b.getContext),
                                    b.getVariable("filteredItems"),
                                    b.joinLists(
                                        b.list([
                                            b.object({
                                                name: b.constant("—"),
                                                value: b.getVariable("noneValue"),
                                            }),
                                        ]),
                                        b.getVariable("filteredItems")
                                    )
                                ),
                            ],
                        ],
                        b.smallList(
                            b.getVariable("choices"),
                            b.holdContainer(
                                b.let(
                                    [
                                        [
                                            "isNone",
                                            b.areIdentical(
                                                b.getVariable("noneValue"),
                                                b.getNonTableProperty("value", b.getContext)
                                            ),
                                        ],
                                    ],
                                    b.adaptiveListItem(
                                        b.getNonTableProperty("name", b.getContext),
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        b.sequence([
                                            b.if(
                                                b.getVariable("isNone"),
                                                b.set(b.getVariable("value"), b.constant("")),
                                                b.set(
                                                    b.getVariable("value"),
                                                    b.getNonTableProperty("value", b.getContext)
                                                )
                                            ),
                                            b.getVariable("onChange"),
                                            b.popModalScreen,
                                        ]),
                                        undefined,
                                        undefined,
                                        b.constant(
                                            ListItemFlags.DrawSeparator +
                                                ListItemFlags.DisableChevron +
                                                ListItemFlags.WrapText
                                        ),
                                        b.if(
                                            b.or(
                                                b.areIdentical(
                                                    b.getVariable("value"),
                                                    b.getNonTableProperty("value", b.getContext)
                                                ),
                                                b.and(
                                                    b.getVariable("isNone"),
                                                    b.not(b.isNotEmpty(b.getVariable("value")))
                                                )
                                            ),
                                            b.imageAccessory(b.constant("01-check-1"))
                                        ),
                                        ListItemAccessoryPosition.Right,
                                        undefined
                                    )
                                )
                            ),
                            undefined,
                            b.constant(true)
                        )
                    ),
                    menuItems: [
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-accept"),
                            onTap: b.popModalScreen,
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["value", b.getNonTableProperty("value", b.getContext)],
                ["onChange", b.getNonTableProperty("onChange", b.getContext)],
                ["choices", b.getNonTableProperty("choices", b.getContext)],
                ["withSearch", b.isGreaterOrEqualTo(b.listLength(b.getVariable("choices")), b.constant(10))],
                ...searchLets,
            ]),
            title: undefined,
            topBar,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    private makeMultiChoiceScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        const { topBar, searchLets, trimmedSearchVariable } = makeSearchBar(
            b,
            undefined,
            b.getVariable("withSearch"),
            undefined,
            true
        );
        return {
            pivotComponents: [
                {
                    componentName: multiChoiceScreenName,
                    component: b.let(
                        [
                            ["searchProperties", b.constant(["name"])],
                            [
                                "filteredItems",
                                b.filter(
                                    b.getVariable("choices"),
                                    b.propertiesContainString(
                                        b.getContext,
                                        b.getVariable("searchProperties"),
                                        trimmedSearchVariable
                                    )
                                ),
                            ],
                        ],
                        b.smallList(
                            b.getVariable("filteredItems"),
                            b.holdContainer(
                                b.let(
                                    [
                                        [
                                            "isSelected",
                                            b.arrayContains(
                                                b.getVariable("valueArray"),
                                                b.getNonTableProperty("value", b.getContext)
                                            ),
                                        ],
                                        ["currentAsList", b.list([b.getNonTableProperty("value", b.getContext)])],
                                    ],
                                    b.adaptiveListItem(
                                        b.getNonTableProperty("name", b.getContext),
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        b.set(
                                            b.getVariable("value"),
                                            b.clonePrimitive(
                                                b.joinStringsWithSeparator(
                                                    b.primitive("BuildChoices", {
                                                        mapper: b.getNonTableProperty("value", b.getContext),
                                                        list: b.getVariable("choices"),
                                                        existing: b.getVariable("valueArray"),
                                                        add: b.if(
                                                            b.not(b.getVariable("isSelected")),
                                                            b.getVariable("currentAsList")
                                                        ),
                                                        remove: b.if(
                                                            b.getVariable("isSelected"),
                                                            b.getVariable("currentAsList")
                                                        ),
                                                    }),
                                                    b.getNonTableProperty("value", b.getContext),
                                                    b.constant(",")
                                                )
                                            )
                                        ),
                                        undefined,
                                        undefined,
                                        b.constant(
                                            ListItemFlags.DrawSeparator +
                                                ListItemFlags.DisableChevron +
                                                ListItemFlags.WrapText
                                        ),
                                        b.if(b.getVariable("isSelected"), b.imageAccessory(b.constant("01-check-1"))),
                                        ListItemAccessoryPosition.Right,
                                        undefined
                                    )
                                )
                            ),
                            undefined,
                            b.constant(true)
                        )
                    ),
                    menuItems: [
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-accept"),
                            onTap: b.sequence([
                                b.if(
                                    b.not(b.areEqual(b.getVariable("original"), b.getVariable("value"))),
                                    b.sequence([
                                        b.set(b.getVariable("original"), b.getVariable("value")),
                                        b.getVariable("onChange"),
                                    ])
                                ),
                                b.popModalScreen,
                            ]),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["original", b.getNonTableProperty("value", b.getContext)],
                ["value", b.clonePrimitive(b.getVariable("original"))],
                ["valueArray", b.splitString(b.getVariable("value"), b.constant(","))],
                ["onChange", b.getNonTableProperty("onChange", b.getContext)],
                ["choices", b.getNonTableProperty("choices", b.getContext)],
                ["withSearch", b.isGreaterOrEqualTo(b.listLength(b.getVariable("choices")), b.constant(10))],
                ...searchLets,
            ]),
            title: undefined,
            topBar,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    private makeEventPickerScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return {
            pivotComponents: [
                {
                    componentName: eventPickerScreenName,
                    component: b.column([
                        b.ui("EventPicker", {
                            selectionStart: b.getVariable("startValueCopy"),
                            selectionEnd: b.getVariable("endValueCopy"),
                            events: b.getVariable("events"),
                        }),
                    ]),
                    menuItems: [
                        {
                            title: b.localizedString("cancel"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-cancel"),
                            onTap: b.popModalScreen,
                        },
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-check"),
                            style: b.constant("platform-accept"),
                            purpose: b.constant(MenuItemPurpose.SetValue),
                            onTap: b.sequence([
                                b.set(b.getVariable("startValue"), b.getVariable("startValueCopy")),
                                b.set(b.getVariable("endValue"), b.getVariable("endValueCopy")),
                                b.getVariable("onChange"),
                                b.popModalScreen,
                            ]),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["startValue", b.getNonTableProperty("startValue", b.getContext)],
                ["startValueCopy", b.clonePrimitive(b.getVariable("startValue"))],
                ["endValue", b.getNonTableProperty("endValue", b.getContext)],
                ["endValueCopy", b.clonePrimitive(b.getVariable("endValue"))],
                ["events", b.getNonTableProperty("events", b.getContext)],
                ["isRequired", b.getNonTableProperty("isRequired", b.getContext)],
                ["onChange", b.getNonTableProperty("onChange", b.getContext)],
            ]),
            topBar: undefined,
            title: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable, ScreenFlag.IsFinalTransient],
        };
    }

    public makePushSendAppFeedbackScreen(): SerializedComponent {
        const { b } = this;

        return b.pushBuilderScreen(
            sendAppFeedbackScreenName,
            b.object({}),
            b.localizedString("sendFeedback"),
            undefined
        );
    }

    private makeShareScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        let onReport: SerializedComponent | undefined;
        if (getFeatureSetting("reportThisApp")) {
            onReport = b.pushBuilderScreen(
                reportAppScreenName,
                b.object({}),
                b.localizedString("reportThisApp"),
                undefined
            );
        }

        return {
            pivotComponents: [
                {
                    componentName: shareScreenName,
                    component: b.ui("ShareScreen", {
                        title: b.getNonTableProperty("title", b.lookupGlobalVariable(GlobalVariableNames.AppMetadata)),
                        author: b.getNonTableProperty(
                            "author",
                            b.lookupGlobalVariable(GlobalVariableNames.AppMetadata)
                        ),
                        displayQRCode: b.getNonTableProperty("displayQRCode", b.getContext),
                        onFeedback: this.makePushSendAppFeedbackScreen(),
                        onReport,
                    }),
                    menuItems: [
                        {
                            title: b.localizedString("done"),
                            icon: b.icon("00-01-glide-close"),
                            style: b.constant("platform-accept"),
                            onTap: b.popModalScreen,
                        },
                        {
                            title: b.constant("qr"),
                            icon: b.if(
                                b.getNonTableProperty("displayQRCode", b.getContext),
                                b.icon("01-14-information-circle"),
                                b.icon("09-06-qr-code")
                            ),
                            style: b.constant("menu"),
                            onTap: b.set(
                                b.getNonTableProperty("displayQRCode", b.getContext),
                                b.not(b.getNonTableProperty("displayQRCode", b.getContext))
                            ),
                            isVisible: b.not(
                                b.getNonTableProperty(
                                    "disableSharing",
                                    b.lookupGlobalVariable(GlobalVariableNames.AppFeatures)
                                ),
                                true
                            ),
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: undefined,
            title: b.localizedString("about"),
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsShareScreen],
        };
    }

    private makeReportOrFeedbackScreen(
        screenName: string,
        lets: readonly LetBinding[],
        inBetweenComponents: readonly SerializedComponent[],
        validation: SerializedComponent | undefined,
        onSend: SerializedComponent
    ): ScreenWithMenuItemsContent {
        const { b } = this;

        let isEnabled = b.and(b.getVariable("emailValid"), b.isNotEmpty(b.getVariable("message")));
        if (validation !== undefined) {
            isEnabled = b.and(isEnabled, validation);
        }
        return {
            pivotComponents: [
                {
                    componentName: screenName,
                    component: b.column([
                        b.ui("EmailField", {
                            title: b.localizedString("from"),
                            placeholder: b.constant(""),
                            email: b.getVariable("finalEmail"),
                            isEnabled: b.not(b.isNotEmpty(b.getVariable("userEmail"))),
                            onChange: b.noAction(),
                            isRequired: b.constant(true),
                            validFlag: b.getVariable("emailValid"),
                        }),
                        ...inBetweenComponents,
                        b.ui("TextField", {
                            title: b.localizedString("message"),
                            placeholder: b.constant(""),
                            size: b.constant(TextEntrySize.Large),
                            text: b.getVariable("message"),
                        }),
                    ]),
                    menuItems: [
                        {
                            title: b.localizedString("send"),
                            icon: b.icon("00-01-glide-check"),
                            style: b.constant("platform-accept"),
                            // It's not technically saving a row, but it does
                            // need to be online.
                            purpose: b.constant(MenuItemPurpose.SaveRow),
                            onTap: b.sequence([
                                onSend,
                                b.popScreen,
                                b.showToast(b.localizedString("formSubmissionSuccess"), true),
                            ]),
                            isEnabled,
                        },
                    ],
                },
            ],
            additionalComponents: undefined,
            ctxModifier: b.captureLets([
                ["from", b.data("")],
                ["emailValid", b.data(false)],
                ["message", b.data("")],
                ...lets,
                ["userEmail", b.realEmailAddress()],
                [
                    "finalEmail",
                    b.if(b.isNotEmpty(b.getVariable("userEmail")), b.getVariable("userEmail"), b.getVariable("from")),
                ],
            ]),
            title: undefined,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUnconfigurable],
        };
    }

    private makeSendAppFeedbackScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return this.makeReportOrFeedbackScreen(
            sendAppFeedbackScreenName,
            [],
            [],
            undefined,
            b.let(
                [["from", b.withDefault(b.verifiedEmailAddress(), b.getVariable("finalEmail"))]],
                b.action("SendAppFeedback", {
                    from: b.getVariable("from"),
                    message: b.getVariable("message"),
                })
            )
        );
    }

    private makeReportAppScreen(): ScreenWithMenuItemsContent {
        const { b } = this;

        return this.makeReportOrFeedbackScreen(
            reportAppScreenName,
            [["reason", b.data("")]],
            [
                b.ui("Choice", {
                    caption: b.localizedString("reason"),
                    value: b.getVariable("reason"),
                    choices: b.list([
                        b.localizedString("displaysInappropriateContent"),
                        b.localizedString("impersonationAndIntellectualProperty"),
                        b.localizedString("privacySecurityOrDeception"),
                        b.localizedString("spamAndMinimumFunctionality"),
                        b.localizedString("other"),
                    ]),
                    isRequired: b.constant(true),
                }),
            ],
            b.isNotEmpty(b.getVariable("reason")),
            b.action("ReportApp", {
                from: b.getVariable("finalEmail"),
                message: b.getVariable("message"),
                reason: b.getVariable("reason"),
            })
        );
    }

    public get userProfileTableInfo(): UserProfileTableInfo | undefined {
        return getUserProfileTableInfo(this.appDescription);
    }

    public makeUserProfileRow(): UserProfileRowBuild | undefined {
        const tableName = this.userProfileTableInfo?.tableName;
        if (tableName === undefined) return undefined;

        const table = findTable(this.schema, tableName);
        if (table === undefined) return undefined;

        return { row: this.b.userProfileRow, table };
    }

    private makeUserProfileScreen(
        screenName: string,
        desc: ClassScreenDescription | undefined
    ): ScreenWithMenuItemsContent {
        const { b } = this;

        let component: SerializedComponent;
        let ctxModifier: SerializedComponent | undefined;
        let title: SerializedComponent | undefined;
        let editItem: SerializedMenuItem | undefined;
        const userProfile = this.makeUserProfileRow();

        if (userProfile !== undefined) {
            this.registerTableUsed(getTableName(userProfile.table));
        }

        if (desc === undefined || userProfile === undefined) {
            component = b.userProfile(
                b.fallbackUsername(),
                b.realEmailAddress(),
                undefined,
                undefined,
                undefined,
                false
            );
            ctxModifier = undefined;
        } else {
            editItem = this.makeEditMenuItem(desc, userProfile.table, b.singletonItem(userProfile.row));

            const maybeComponents = this.makeClassScreenComponents(
                screenName,
                desc,
                this.addDataToContextModifier(userProfile.row),
                editItem !== undefined
                    ? [
                          b.button(
                              b.localizedString("signOut"),
                              Appearance.Transparent,
                              undefined,
                              undefined,
                              b.action("SignOut", {}),
                              {}
                          ),
                      ]
                    : []
            );
            if (typeof maybeComponents === "string") return makeErrorScreen(b, screenName, maybeComponents);

            component = maybeComponents.component;
            ctxModifier = maybeComponents.ctxModifier;
            title = maybeComponents.title;
        }
        if (title === undefined) {
            title = b.localizedString("profile");
        }
        title = b.withDefault(b.getNonTableProperty("title", b.getContext), title);

        const menuItems: SerializedMenuItem[] = [
            {
                title: b.localizedString("close"),
                icon: b.icon("00-01-glide-close"),
                style: b.constant("platform-cancel"),
                onTap: b.popModalScreen,
            },
        ];

        if (editItem === undefined) {
            menuItems.push({
                title: b.localizedString("signOut"),
                icon: b.constant("FIXME: Put in some icon"),
                onTap: b.action("SignOut", {}),
                isEnabled: b.constant(true),
                isVisible: b.constant(true),
                purpose: b.constant(MenuItemPurpose.SignOut),
                style: b.constant("text"),
            });
        } else {
            menuItems.push(editItem);
        }

        return {
            pivotComponents: [
                {
                    componentName: screenName,
                    component,
                    menuItems,
                },
            ],
            additionalComponents: undefined,
            ctxModifier,
            title,
            topBar: undefined,
            pivotPicker: undefined,
            quotaBannerMessage: b.constant(false),
            flags: [ScreenFlag.IsUserProfileScreen],
        };
    }

    public makeScreen(screenName: string): ScreenWithMenuItemsContent {
        const { b } = this;

        // FIXME: This shouldn't be special-cased here
        if (screenName === emailAuthScreenName) {
            return this.makeEmailAuthScreen();
        } else if (screenName === buyScreenName) {
            return this.makeBuyScreen();
        } else if (screenName === dynamicFilterScreenName) {
            return this.makeDynamicFilterScreen();
        } else if (screenName === overlayEditorScreenName) {
            return this.makeOverlayEditorScreen();
        } else if (screenName === choiceScreenName) {
            return this.makeChoiceScreen();
        } else if (screenName === multiChoiceScreenName) {
            return this.makeMultiChoiceScreen();
        } else if (screenName === webViewScreenName) {
            return this.makeWebViewScreen();
        } else if (screenName === signaturePadScreenName) {
            return this.makeSignaturePadScreen();
        } else if (screenName === codeScannerScreenName) {
            return this.makeCodeScannerScreen();
        } else if (screenName === liveLinearBarcodeScannerScreenName) {
            return this.makeLiveLinearBarcodeScannerScreen();
        } else if (screenName === eventPickerScreenName) {
            return this.makeEventPickerScreen();
        } else if (screenName === shareScreenName) {
            return this.makeShareScreen();
        } else if (screenName === sendAppFeedbackScreenName) {
            return this.makeSendAppFeedbackScreen();
        } else if (screenName === reportAppScreenName) {
            return this.makeReportAppScreen();
        }

        try {
            const desc = this.appDescription.screenDescriptions[screenName] as ScreenDescription | undefined;

            if (screenName === userProfileScreenName) {
                if (desc !== undefined && desc.kind !== ScreenDescriptionKind.Class) {
                    this.error(`User profile screen is not a detail screen`);
                    return makeErrorScreen(b, screenName, screenErrorInternal);
                }
                return this.makeUserProfileScreen(screenName, desc);
            }

            if (desc === undefined) {
                this.error(`Screen description not present for screen ${screenName}`);
                return makeErrorScreen(b, screenName, "This screen has gone missing.");
            }

            let pivotComponents: ReadonlyArray<PivotContent>;
            let additionalComponents: ReadonlyMap<string, SerializedComponent> | undefined;
            let ctxModifier: SerializedComponent | undefined;
            let title: SerializedComponent | undefined;
            let topBar: SerializedComponent | undefined;
            let pivotPicker: SerializedComponent | undefined;
            let quotaBannerMessage: SerializedComponent | undefined;
            let flags: ScreenFlag[] | undefined;
            switch (desc.kind) {
                case ScreenDescriptionKind.Class: {
                    let result: ScreenWithMenuItemsContent;
                    if (isFormScreen(desc) || isAddClassScreenName(screenName)) {
                        result = this.addOrFormScreenForClass(screenName, desc as EditScreenDescription);
                    } else if (isEditClassScreenName(screenName)) {
                        result = this.editScreenForClass(screenName, desc as EditScreenDescription);
                    } else {
                        result = this.screenForClass(screenName, desc);
                    }
                    pivotComponents = result.pivotComponents;
                    additionalComponents = result.additionalComponents;
                    ctxModifier = result.ctxModifier;
                    title = result.title;
                    topBar = result.topBar;
                    pivotPicker = result.pivotPicker;
                    quotaBannerMessage = result.quotaBannerMessage;
                    flags = Array.from(result.flags);
                    break;
                }
                case ScreenDescriptionKind.Array: {
                    const result = this.screenForArray(screenName, desc);
                    pivotComponents = result.pivotComponents;
                    additionalComponents = result.additionalComponents;
                    ctxModifier = result.ctxModifier;
                    title = result.title;
                    topBar = result.topBar;
                    pivotPicker = result.pivotPicker;
                    quotaBannerMessage = result.quotaBannerMessage;
                    flags = Array.from(result.flags);
                    break;
                }
                case ScreenDescriptionKind.Chat:
                    pivotComponents = [
                        {
                            componentName: screenName,
                            component: this.screenForChat(desc),
                            menuItems: [],
                        },
                    ];
                    title = this._getScreenTitle;
                    break;
                case ScreenDescriptionKind.ShoppingCart:
                    const result = this.screenForShoppingCart(screenName, desc);
                    pivotComponents = result.pivotComponents;
                    additionalComponents = result.additionalComponents;
                    ctxModifier = result.ctxModifier;
                    title = result.title;
                    topBar = result.topBar;
                    pivotPicker = result.pivotPicker;
                    quotaBannerMessage = result.quotaBannerMessage;
                    flags = Array.from(result.flags);
                    break;
                default:
                    return proveNever(
                        desc,
                        "We were asked to build a primitive screen",
                        makeErrorScreen(b, screenName, screenErrorInternal)
                    );
            }
            return {
                pivotComponents,
                additionalComponents,
                ctxModifier,
                title,
                topBar,
                pivotPicker,
                quotaBannerMessage: quotaBannerMessage ?? b.constant(false),
                flags: flags ?? [],
            };
        } catch (e: unknown) {
            this.error(`Error while building screen ${screenName}: ${exceptionToString(e)}`);
            if (this._printErrors && hasOwnProperty(e, "stack") && e.stack !== undefined) {
                logError(e.stack);
            }
            return makeErrorScreen(b, screenName, screenErrorInternal);
        }
    }

    private commitScreen(
        screenName: string,
        screenParts: ScreenWithMenuItemsContent,
        isUsedInApp: boolean
    ): SerializedScreen {
        const {
            pivotComponents,
            additionalComponents,
            ctxModifier,
            title,
            topBar,
            pivotPicker,
            quotaBannerMessage,
            flags,
        } = screenParts;

        for (const { componentName, component } of pivotComponents) {
            this._components.set(componentName, component);
        }

        if (additionalComponents !== undefined) {
            for (const [componentName, content] of additionalComponents.entries()) {
                this._components.set(componentName, content);
            }
        }

        const pivots: SerializedPivot[] = pivotComponents.map(({ componentName, menuItems }) => ({
            componentName,
            menuItems,
        }));
        const screen: SerializedScreen = { pivots, ctxModifier, title, topBar, pivotPicker, quotaBannerMessage, flags };
        this._screens.set(screenName, screen);
        if (
            isUsedInApp &&
            this.appDescription.screenDescriptions[screenName] !== undefined &&
            !flags.includes(ScreenFlag.IsErrorScreen)
        ) {
            this._screensUsedInApp.add(screenName);
        }

        return screen;
    }

    private makeAndCommitScreen(screenName: string): SerializedScreen {
        assert(this._currentlyBuildingScreenName === undefined);
        try {
            this._currentlyBuildingScreenName = screenName;
            return this.commitScreen(screenName, this.makeScreen(screenName), true);
        } finally {
            this._currentlyBuildingScreenName = undefined;
        }
    }

    private makeColumnData(): { computedColumns: readonly SerializedComputedColumn[] } {
        const { b } = this;

        const columnsSeen = new EqualitySet<[string, string]>();
        const toDoQueue: [string, string][] = [];

        const computedColumns: SerializedComputedColumn[] = [];
        for (;;) {
            if (this.isBuilder) {
                for (const t of this.schema.tables) {
                    const tableName = getTableName(t);
                    for (const c of t.columns) {
                        const tableColumnName: [string, string] = [tableName.name, c.name];
                        if (columnsSeen.has(tableColumnName)) continue;
                        toDoQueue.push(tableColumnName);
                    }
                }
            } else {
                for (const tableColumnName of this.b.getColumnsUsed()) {
                    if (columnsSeen.has(tableColumnName)) continue;
                    toDoQueue.push(tableColumnName);
                }
            }
            if (toDoQueue.length === 0) break;

            for (;;) {
                const tableColumnName = toDoQueue.pop();
                if (tableColumnName === undefined) break;

                columnsSeen.add(tableColumnName);

                // FIXME: We're not handling special tables correctly here
                const tableName = makeTableName(tableColumnName[0]);
                const table = findTable(this.schema, tableName);
                if (table === undefined) continue;

                const column = getTableColumn(table, tableColumnName[1]);
                if (column === undefined) continue;

                const { formula } = column;
                if (formula === undefined) continue;

                let value: SerializedComponent;
                const result = compileFormula(
                    this,
                    table,
                    formula,
                    true,
                    false,
                    makeUniqueIDForColumnFormula(tableName, column.name)
                );
                if (result.success) {
                    value = b.primitive("Pin", { value: result.value });
                } else {
                    this.error("Could not compile formula", JSON.stringify(result));
                    value = b.constant(null);
                }
                computedColumns.push({ table: makeTableRef(table), columnName: column.name, value });
            }
        }

        return { computedColumns };
    }

    public compileTabVisibilityFilters(
        filters: readonly ArrayTransform[] | undefined
    ): SerializedComponent | undefined {
        return compileFilters(this, undefined, this.b.constant({}), filters ?? []);
    }

    private buildRoot(
        tabDescriptions: ReadonlyArray<TabDescription>,
        buildAllTabs: boolean = true
    ): {
        rootScreens: SerializedRootScreen[];
        screens: { [name: string]: SerializedScreen };
        components: SerializedComponentsObject;
    } {
        const { b } = this;
        const noTab: SerializedRootScreen = {
            pivots: [],
        };

        // We have to build a `SerializedRootScreen` for each tab description,
        // because the frontend assumes they're in line.
        const tabs: SerializedRootScreen[] = tabDescriptions.map(t => {
            const screenName = getScreenProperty(t.screenName);
            if (screenName === undefined) return noTab;

            if (t.hidden && !buildAllTabs) {
                // In very old apps, tab root screens can still be default
                // class and array screens, but those can be used in other
                // parts of the app, too, so we don't want to build error
                // screens for them.
                if (!isFreeScreenName(screenName)) return noTab;

                return {
                    ...this.commitScreen(
                        screenName,
                        makeErrorScreen(b, screenName, "You should not be able to see this screen."),
                        false
                    ),
                    isVisible: undefined,
                };
            }

            return {
                ...this.makeAndCommitScreen(screenName),
                // This must be synchronized with `buildScreen` in `BuilderGlideApp.tsx`
                ctxFromRootContext: this.makeScreenContextForTab(t),
                isVisible: this.compileTabVisibilityFilters(t.visibilityFilters),
            };
        });
        return { rootScreens: tabs, screens: mapToObject(this._screens), components: mapToObject(this._components) };
    }

    // FIXME: buildAllTabs is stupid.  We have a `hidden` flag in `TabDescription`,
    // but we build all tab screens anyway because we assume that the indices into
    // the tab descriptions and the screens is the same.  For refreshing app data,
    // however, we must only make the screens that are actually used, or we will fail
    // to refresh when sheets changed that we shouldn't care about.
    //
    // Ideally we wouldn't have tab descriptions for "hidden" tabs at all.
    public buildApp(buildAllTabs: boolean = true, buildDefaultScreens: boolean = false): SerializedApp {
        const { appDescription } = this;
        const tabs = getAppTabs(appDescription);
        const { rootScreens } = this.buildRoot(tabs, buildAllTabs);
        const root = { ctx: this.makeRootContext(), tabs: rootScreens };

        this.makeAndCommitScreen(emailAuthScreenName);
        this.makeAndCommitScreen(buyScreenName);
        this.makeAndCommitScreen(dynamicFilterScreenName);
        this.makeAndCommitScreen(overlayEditorScreenName);
        this.makeAndCommitScreen(choiceScreenName);
        this.makeAndCommitScreen(multiChoiceScreenName);
        this.makeAndCommitScreen(userProfileScreenName);
        this.makeAndCommitScreen(webViewScreenName);
        this.makeAndCommitScreen(signaturePadScreenName);
        this.makeAndCommitScreen(eventPickerScreenName);
        this.makeAndCommitScreen(shareScreenName);
        this.makeAndCommitScreen(sendAppFeedbackScreenName);
        this.makeAndCommitScreen(reportAppScreenName);
        this.makeAndCommitScreen(codeScannerScreenName);
        this.makeAndCommitScreen(liveLinearBarcodeScannerScreenName);

        if (buildDefaultScreens) {
            for (const table of this.schema.tables) {
                const tableRef = makeTableRef(table);
                this.makeAndCommitScreen(arrayScreenName(this, tableRef));
                this.makeAndCommitScreen(classScreenName(tableRef));
                this.makeAndCommitScreen(addClassScreenName(tableRef));
                this.makeAndCommitScreen(editClassScreenName(tableRef));
            }
        }

        for (;;) {
            const screenName = this._screensToBuild.pop();
            if (screenName === undefined) break;
            if (this._components.has(screenName)) continue;

            this.makeAndCommitScreen(screenName);
        }

        const functions = makeReusableComponents(this);
        Object.assign(functions, mapToObject(this._components));
        const screens = mapToObject(this._screens);

        assert(this._screensToBuild.length === 0);

        return {
            ...appDescription,
            tabs,
            root: defined(root),
            // NOTE: This must not be constructed as `undefined`, even though
            // our type allows it.  See poison pill comment in `SerializedApp.ts`.
            functions,
            screens,
            features: this._appFeatures,
            builderActions: undefined,
            ...this.makeColumnData(),
        };
    }

    // This can be safely deleted when we get rid of ##getScreensUsedByAppOld.
    public getScreensUsed(withAllTabsAndDefaultScreens: boolean): ReadonlyArray<string> {
        this.buildApp(withAllTabsAndDefaultScreens, withAllTabsAndDefaultScreens);
        return Array.from(this._screensUsedInApp);
    }

    public get tablesUsed(): readonly TableName[] {
        return this._tablesUsed;
    }
}

export class NonBuildingAppBuilder extends AppBuilder {
    constructor(adc: ExistingAppDescriptionContext, appFeatures: AppFeatures, eminenceFlags: EminenceFlags) {
        super(adc, eminenceFlags, false, appFeatures, false, false);
    }

    public getCompiledJSCode(): SerializedComponent | undefined | false {
        return false;
    }
}

export class PublishingAppBuilder extends AppBuilder {
    private readonly _jsCompiledFormulas = new Map<string, SerializedComponent | false>();
    private readonly _jsCode: string[] = [];

    constructor(adc: ExistingAppDescriptionContext, appFeatures: AppFeatures, eminenceFlags: EminenceFlags) {
        super(adc, eminenceFlags, false, appFeatures, false, true);
    }

    public get compiledJavaScript(): string {
        const calls = this._jsCode.map(f => `${f}(callbacks);\n`);
        return `(function (callbacks) {
            ${calls.join("")}
        })`;
    }

    public getCompiledJSCode(name: string): SerializedComponent | undefined | false {
        return this._jsCompiledFormulas.get(name);
    }

    public setCompiledJSFailed(name: string): void {
        this._jsCompiledFormulas.set(name, false);
    }

    public setCompiledJavaScript(name: string, code: string, serializedComponent: SerializedComponent): void {
        this._jsCompiledFormulas.set(name, serializedComponent);
        this._jsCode.push(code);
    }
}

export class BuilderAppBuilder extends AppBuilder {
    constructor(
        adc: ExistingAppDescriptionContext,
        appFeatures: AppFeatures,
        eminenceFlags: EminenceFlags,
        isBuilder: boolean = true
    ) {
        super(adc, eminenceFlags, isBuilder, appFeatures, true, true);
    }

    public getCompiledJSCode(name: string): SerializedComponent | undefined | false {
        return builderJSCompiledFormulas.get(name);
    }

    public setCompiledJSFailed(name: string): void {
        builderJSCompiledFormulas.set(name, false);
    }

    public setCompiledJavaScript(name: string, code: string, serializedComponent: SerializedComponent): void {
        builderJSCompiledFormulas.set(name, serializedComponent);
        // eslint-disable-next-line no-eval
        eval(code)(compiledJSCallbacks);
    }
}

export class AIAppBuilder extends BuilderAppBuilder {
    constructor(adc: ExistingAppDescriptionContext, appFeatures: AppFeatures, eminenceFlags: EminenceFlags) {
        super(adc, appFeatures, eminenceFlags, false);
    }
}
// ##getScreensUsedByAppOld:
// This is superfluous at this point.  We only keep it around in case we want
// to compare the new code against the old one, if we suspect a bug.  If this
// is in the way of anything, just get rid of it.
export function getScreensUsedByAppOld(
    adc: ExistingAppDescriptionContext,
    appFeatures: AppFeatures,
    eminenceFlags: EminenceFlags,
    withAllTabsAndDefaultScreens: boolean
): readonly [string, ScreenDescription][] {
    const builder = new NonBuildingAppBuilder(adc, appFeatures, eminenceFlags);
    return builder
        .getScreensUsed(withAllTabsAndDefaultScreens)
        .map(n => [n, defined(adc.appDescription.screenDescriptions[n])]);
}
